import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Users,
  Building2,
  CalendarCheck,
  DollarSign,
  ShieldCheck,
  MessageSquareWarning,
  RefreshCw,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { StatCard } from "@/components/admin/StatCard";
import { ActivityFeed } from "@/components/admin/ActivityFeed";
import type { AuditLogItem } from "@/components/admin/ActivityFeed";
import {
  getDashboardStats,
  getRecentActivity,
  getTopBuildings,
  getRecentFailedPayments,
  getSignupsPerDay,
  getBookingsPerWeek,
  getUsersByPlan,
  getMRRByMonth,
} from "@/lib/admin/queries";
import { markAsPaid } from "@/lib/admin/actions";
import { useAuth } from "@/contexts/AuthContext";

const PLAN_COLORS: Record<string, string> = {
  strata: "#3b82f6",
  property_manager: "#8b5cf6",
  individual: "#10b981",
  host: "#f59e0b",
  unknown: "#6b7280",
};

function complianceColor(pct: number) {
  if (pct >= 80) return "text-green-600";
  if (pct >= 60) return "text-amber-600";
  return "text-red-600";
}

export default function Dashboard() {
  const { profile } = useAuth();
  const actorId = profile?.id ?? "";

  const statsQuery = useQuery({
    queryKey: ["admin", "dashboard-stats"],
    queryFn: getDashboardStats,
  });

  const activityQuery = useQuery({
    queryKey: ["admin", "recent-activity"],
    queryFn: () => getRecentActivity(20),
  });

  const topBuildingsQuery = useQuery({
    queryKey: ["admin", "top-buildings"],
    queryFn: () => getTopBuildings(5),
  });

  const failedPaymentsQuery = useQuery({
    queryKey: ["admin", "failed-payments"],
    queryFn: () => getRecentFailedPayments(10),
  });

  const signupsQuery = useQuery({
    queryKey: ["admin", "signups-per-day"],
    queryFn: () => getSignupsPerDay(30),
  });

  const bookingsWeeklyQuery = useQuery({
    queryKey: ["admin", "bookings-per-week"],
    queryFn: () => getBookingsPerWeek(12),
  });

  const usersByPlanQuery = useQuery({
    queryKey: ["admin", "users-by-plan"],
    queryFn: getUsersByPlan,
  });

  const mrrByMonthQuery = useQuery({
    queryKey: ["admin", "mrr-by-month"],
    queryFn: () => getMRRByMonth(6),
  });

  const retryPaymentMutation = useMutation({
    mutationFn: (subscriptionId: string) => markAsPaid(subscriptionId, actorId),
    onSuccess: () => {
      toast.success("Payment marked as paid");
      failedPaymentsQuery.refetch();
    },
    onError: () => toast.error("Failed to retry payment"),
  });

  const stats = statsQuery.data?.data;
  const statsLoading = statsQuery.isLoading;

  const activityItems: AuditLogItem[] = (activityQuery.data?.data ?? []).map((item) => ({
    id: item.id,
    action: item.action,
    entity_type: item.entity ?? "",
    entity_id: "",
    actor_name: item.actor?.full_name ?? item.user_name ?? "System",
    created_at: item.created_at,
    details: null,
  }));

  const signupData = (signupsQuery.data?.data ?? []).map((d) => ({
    date: d.date.slice(5),
    signups: d.count,
  }));

  const bookingData = (bookingsWeeklyQuery.data?.data ?? []).map((d) => ({
    week: d.week.slice(5),
    bookings: d.count,
  }));

  const planData = (usersByPlanQuery.data?.data ?? []).map((d) => ({
    name: d.plan.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    value: d.count,
    key: d.plan,
  }));

  const mrrData = (mrrByMonthQuery.data?.data ?? []).map((d) => ({
    month: d.month,
    mrr: d.mrr,
  }));

  const topBuildings = topBuildingsQuery.data?.data ?? [];
  const failedPayments = failedPaymentsQuery.data?.data ?? [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            statsQuery.refetch();
            activityQuery.refetch();
          }}
          className="whitespace-nowrap"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard
          title="Total Users"
          value={statsLoading ? "—" : (stats?.totalUsers ?? 0).toLocaleString()}
          change={stats?.userGrowth}
          changeLabel="vs last month"
          icon={<Users className="h-5 w-5" />}
          color="blue"
          loading={statsLoading}
        />
        <StatCard
          title="Active Buildings"
          value={statsLoading ? "—" : (stats?.activeBuildings ?? 0).toLocaleString()}
          change={stats?.buildingGrowth}
          changeLabel="vs last month"
          icon={<Building2 className="h-5 w-5" />}
          color="purple"
          loading={statsLoading}
        />
        <StatCard
          title="Bookings This Month"
          value={statsLoading ? "—" : (stats?.bookingsThisMonth ?? 0).toLocaleString()}
          change={stats?.bookingGrowth}
          changeLabel="vs last month"
          icon={<CalendarCheck className="h-5 w-5" />}
          color="green"
          loading={statsLoading}
        />
        <StatCard
          title="MRR (CA$)"
          value={statsLoading ? "—" : `CA$${(stats?.mrr ?? 0).toFixed(2)}`}
          change={stats?.mrrGrowth}
          changeLabel="vs last month"
          icon={<DollarSign className="h-5 w-5" />}
          color="amber"
          loading={statsLoading}
        />
        <StatCard
          title="Compliance Rate"
          value={statsLoading ? "—" : `${stats?.complianceRate ?? 0}%`}
          icon={<ShieldCheck className="h-5 w-5" />}
          color="green"
          loading={statsLoading}
        />
        <StatCard
          title="Open Disputes"
          value={statsLoading ? "—" : (stats?.openDisputes ?? 0).toLocaleString()}
          icon={<MessageSquareWarning className="h-5 w-5" />}
          color="red"
          loading={statsLoading}
        />
      </div>

      {/* Charts + Activity Row */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          {/* Signups per day */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">New Signups — Last 30 Days</CardTitle>
            </CardHeader>
            <CardContent>
              {signupsQuery.isLoading ? (
                <Skeleton className="h-48 w-full" />
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={signupData} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11 }} width={30} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ fontSize: 12 }}
                      formatter={(v: number) => [v, "Signups"]}
                    />
                    <Line
                      type="monotone"
                      dataKey="signups"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Bookings per week */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Bookings per Week — Last 12 Weeks</CardTitle>
            </CardHeader>
            <CardContent>
              {bookingsWeeklyQuery.isLoading ? (
                <Skeleton className="h-48 w-full" />
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={bookingData} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="week" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11 }} width={30} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Bar dataKey="bookings" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Activity Feed */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <ActivityFeed
              items={activityItems}
              loading={activityQuery.isLoading}
              maxItems={20}
            />
          </CardContent>
        </Card>
      </div>

      {/* Second Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users by plan */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Users by Plan</CardTitle>
          </CardHeader>
          <CardContent>
            {usersByPlanQuery.isLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : planData.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-sm text-gray-400">
                No subscription data available
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={planData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {planData.map((entry) => (
                      <Cell
                        key={entry.key}
                        fill={PLAN_COLORS[entry.key] ?? PLAN_COLORS.unknown}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ fontSize: 12 }}
                    formatter={(v: number, _: string, props: { payload?: { name?: string } }) => [
                      `${v} users`,
                      props.payload?.name ?? "",
                    ]}
                  />
                  <Legend
                    iconType="circle"
                    iconSize={10}
                    formatter={(value) => (
                      <span style={{ fontSize: 12 }}>{value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* MRR growth */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">MRR Growth — Last 6 Months</CardTitle>
          </CardHeader>
          <CardContent>
            {mrrByMonthQuery.isLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : mrrData.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-sm text-gray-400">
                No MRR data available
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={mrrData} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    width={50}
                    tickFormatter={(v) => `$${v}`}
                  />
                  <Tooltip
                    contentStyle={{ fontSize: 12 }}
                    formatter={(v: number) => [`CA$${v.toFixed(2)}`, "MRR"]}
                  />
                  <Area
                    type="monotone"
                    dataKey="mrr"
                    stroke="#10b981"
                    strokeWidth={2}
                    fill="url(#mrrGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top 5 buildings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top Buildings by Bookings</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {topBuildingsQuery.isLoading ? (
              <div className="p-4 space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : topBuildings.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-400">No buildings yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Building</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Bookings</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Compliance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {topBuildings.map((b) => (
                      <tr key={b.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5">
                          <p className="font-medium text-gray-900 truncate max-w-[120px]">{b.name}</p>
                          <p className="text-xs text-gray-400 truncate max-w-[120px]">{b.address ?? "—"}</p>
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-700 tabular-nums">
                          <span className="whitespace-nowrap">{b.booking_count}</span>
                        </td>
                        <td className={`px-4 py-2.5 text-right font-medium tabular-nums ${complianceColor(b.compliance_pct)}`}>
                          <span className="whitespace-nowrap">{b.compliance_pct}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent failed payments */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Failed Payments</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {failedPaymentsQuery.isLoading ? (
              <div className="p-4 space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : failedPayments.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-400">No failed payments</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">User</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {failedPayments.slice(0, 5).map((p) => (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5">
                          <p className="font-medium text-gray-900 truncate max-w-[120px]">
                            {p.profile?.full_name ?? "Unknown"}
                          </p>
                          <p className="text-xs text-red-500">Past due</p>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs whitespace-nowrap"
                            onClick={() => retryPaymentMutation.mutate(p.id)}
                            disabled={retryPaymentMutation.isPending}
                          >
                            Mark Paid
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Compliance violations */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Violations (7 days)</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {activityQuery.isLoading ? (
              <div className="p-4 space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : (
              (() => {
                const violations = (activityQuery.data?.data ?? []).filter((item) => {
                  const action = (item.action ?? "").toLowerCase();
                  const sevenDaysAgo = new Date();
                  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                  return (
                    (action.includes("violat") || action.includes("over_limit") || action.includes("breach")) &&
                    new Date(item.created_at) >= sevenDaysAgo
                  );
                });
                return violations.length === 0 ? (
                  <div className="py-10 text-center text-sm text-gray-400">No violations in last 7 days</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Event</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Type</th>
                          <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                            <Badge variant="outline" className="text-xs">7d</Badge>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {violations.slice(0, 5).map((v) => (
                          <tr key={v.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2.5">
                              <p className="font-medium text-gray-900 text-xs truncate max-w-[100px]">
                                {v.user_name ?? "Unknown"}
                              </p>
                            </td>
                            <td className="px-4 py-2.5">
                              <span className="text-xs text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full">
                                {v.entity ?? "N/A"}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-right text-xs text-gray-400">
                              <span className="whitespace-nowrap">{new Date(v.created_at).toLocaleDateString("en-CA")}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
